import HttpClient, { isAuthError } from '@seek/ca-http-client';
import { useHubble } from '@seek/hubble';
import type { SolMetadata } from '@seek/sol-js';
import { useEffect, useState } from 'react';

import { useConfig } from 'src/context/config';
import { useJobId } from 'src/context/useJobId';
import { useGetSignInUrl } from 'src/hooks/useGetSignInUrl';
import { getJobSolMetadata } from 'src/modules/solTracker/solMetadata';
import type { Config } from 'src/types';
import { getSessionId } from 'src/utils';

export function useLinkoutUrl() {
  const jobId = useJobId();
  const hubble = useHubble();
  const { config } = useConfig();
  const [error, setError] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [requireAuth, setRequireAuth] = useState(false);
  const signInUrl = useGetSignInUrl();

  useEffect(() => {
    let ignore = false;
    const fetchUrl = async () => {
      setLoading(true);

      const solMetadata = getJobSolMetadata(jobId);

      try {
        const linkoutUrl = await fetchLinkoutUrl({
          jobId,
          config,
          visitorId: hubble.visitorId(),
          solMetadata,
          sessionId: getSessionId(),
        });

        if (ignore) return;

        setUrl(linkoutUrl);
      } catch (err: any) {
        if (ignore) return;

        if (isAuthError(err)) {
          setUrl(signInUrl);

          return setRequireAuth(true);
        }

        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();

    return () => {
      ignore = true;
    };
  }, [config, hubble, jobId, signInUrl]);

  return { url, loading, error, requireAuth };
}

async function fetchLinkoutUrl({
  config,
  visitorId,
  solMetadata,
  jobId,
  sessionId,
}: {
  config: Config;
  visitorId: string;
  solMetadata?: SolMetadata;
  jobId: string;
  sessionId?: string;
}) {
  const httpClient = new HttpClient({
    appName: 'SEEK JobApplyFrontend',
    authOptions: {
      useMockedAuth: config.ENV !== 'production',
      forceStrategy: 'AUTH0',
    },
  });
  const resp = await httpClient.send({
    method: 'post',
    url: `${config.LINKOUT_SERVICE_URL}/job/${jobId}/authenticated`,
    data: { solMetadata, visitorid: visitorId },
    ...(sessionId ? { headers: { 'X-Seek-EC-SessionId': sessionId } } : {}),
  });

  if (!resp?.data?.href) {
    throw new Error('no linkout href');
  }

  return resp.data.href;
}
